import { motion } from 'framer-motion'

const recentChats = [
  'Cozy fall layers',
  'Fall camping in Colorado',
  'Ultralight backpacking kit',
  'Best 4-season tents',
  'Basecamp cooking gear',
  'Trail running shoes',
  'Winter sleeping bags',
]

const products = [
  { name: 'Trail Ridge Daypack', variant: 'Charcoal — 24L', price: '$89.00' },
  { name: 'Campfire Mug Set', variant: 'Cream — 4pk', price: '$28.00' },
  { name: 'Basecamp Lantern', variant: 'Brass — 300lm', price: '$42.00' },
]

const cartItems = [
  { name: 'Trail Ridge Daypack', variant: 'Charcoal — 24L', price: '$89.00' },
  { name: 'Campfire Mug Set', variant: 'Cream — 4pk', price: '$28.00' },
]

const popIn = {
  hidden: { scale: 0 },
  visible: {
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.34, 1.56, 0.64, 1] as const,
      staggerChildren: 0.06,
      delayChildren: 0.35,
    },
  },
}

const rowFade = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

export default function DiscoveryMockup() {
  return (
    <div className="relative">
      {/* Chat panel */}
      <motion.div
        variants={popIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="overflow-hidden rounded-2xl border border-oak-black/[0.06] bg-white"
        style={{
          boxShadow:
            '0 4px 16px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.05)',
          transformOrigin: '50% 50%',
        }}
      >
        <div className="grid grid-cols-[128px_1fr] md:grid-cols-[160px_1fr]">
          {/* Sidebar */}
          <aside className="border-r border-oak-black/[0.06] bg-oak-black/[0.015] p-3 md:p-4">
            <motion.div
              variants={rowFade}
              className="text-[13px] font-semibold text-oak-black"
            >
              AI Assistant
            </motion.div>
            <motion.div
              variants={rowFade}
              className="mt-4 flex items-center gap-1.5 rounded-md border border-oak-black/10 bg-white px-2 py-1.5 text-[11px] font-medium text-oak-black/70"
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path
                  d="M11 2l3 3-8 8H3v-3l8-8z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
              New chat
            </motion.div>
            <motion.p
              variants={rowFade}
              className="mt-4 text-[9.5px] font-medium uppercase tracking-wider text-oak-black/40"
            >
              Last 6 months
            </motion.p>
            <ul className="mt-1.5 space-y-0.5">
              {recentChats.map((c, i) => (
                <motion.li
                  key={c}
                  variants={rowFade}
                  className={`truncate rounded px-2 py-1 text-[11px] ${
                    i === 1
                      ? 'bg-oak-black/[0.05] font-medium text-oak-black'
                      : 'text-oak-black/50'
                  }`}
                >
                  {c}
                </motion.li>
              ))}
            </ul>
          </aside>

          {/* Chat body */}
          <div className="flex flex-col gap-2.5 p-3 md:gap-3 md:p-5">
            <motion.div variants={rowFade} className="flex justify-end">
              <div className="max-w-[82%] rounded-xl bg-gold-50 px-3 py-2 text-[11.5px] leading-[1.4] text-oak-black">
                Planning a fall camping trip in Colorado. What are some
                essentials I should grab?
              </div>
            </motion.div>
            <motion.div variants={rowFade} className="flex justify-start">
              <div className="max-w-[90%] rounded-xl border border-oak-black/[0.06] bg-white px-3 py-2 text-[11.5px] leading-[1.4] text-oak-black/80">
                Here are a few essentials from communities on Oak that pair
                well together:
              </div>
            </motion.div>
            <div className="grid grid-cols-3 gap-2">
              {products.map((p) => (
                <motion.div
                  key={p.name}
                  variants={rowFade}
                  className="flex flex-col overflow-hidden rounded-lg border border-oak-black/[0.06] bg-white"
                >
                  <div className="aspect-square bg-oak-cream" />
                  <div className="px-2 py-2">
                    <div className="text-[10.5px] font-semibold leading-tight text-oak-black">
                      {p.name}
                    </div>
                    <div className="text-[10px] text-oak-black/50">
                      {p.variant}
                    </div>
                    <div className="mt-1 text-[11px] font-semibold text-oak-black">
                      {p.price}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1">
                      <div className="flex h-3 w-3 items-center justify-center rounded-full bg-oak-green text-[7px] font-bold text-white">
                        T
                      </div>
                      <span className="text-[9.5px] text-oak-black/60">
                        Trailhead Co.
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div
              variants={rowFade}
              className="mt-1 flex items-center justify-between rounded-lg border border-oak-black/[0.06] px-3 py-2 text-[11px] text-oak-black/40"
            >
              <span>Type your message</span>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 13V3M3 8l5-5 5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Checkout panel — overlapping on md+ */}
      <motion.div
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{
          duration: 0.6,
          delay: 0.55,
          ease: [0.34, 1.56, 0.64, 1] as const,
        }}
        className="mt-4 w-full overflow-hidden rounded-2xl border border-oak-black/[0.06] bg-white p-3.5 md:absolute md:-bottom-10 md:-right-4 md:mt-0 md:w-[58%] md:p-4 lg:-right-6"
        style={{
          boxShadow:
            '0 8px 24px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.05)',
          transformOrigin: '50% 50%',
        }}
      >
        <div className="flex items-center gap-1.5">
          <div className="flex h-4 w-4 items-center justify-center rounded-full bg-oak-green text-[9px] font-bold text-white">
            T
          </div>
          <span className="text-[11px] font-semibold text-oak-black">
            Trailhead Co.
          </span>
        </div>

        <div className="mt-2.5 space-y-2">
          {cartItems.map((c) => (
            <div key={c.name} className="flex items-center gap-2">
              <div className="h-8 w-8 shrink-0 rounded-md bg-oak-cream" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] font-semibold text-oak-black">
                  {c.name}
                </div>
                <div className="truncate text-[10px] text-oak-black/50">
                  {c.variant}
                </div>
                <div className="text-[11px] font-semibold text-oak-black">
                  {c.price}
                </div>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-oak-black/[0.08] px-1.5 py-0.5 text-[11px] text-oak-black/60">
                <span>−</span>
                <span className="font-medium text-oak-black">1</span>
                <span>+</span>
              </div>
            </div>
          ))}
        </div>

        <div className="my-3 h-px bg-oak-black/[0.06]" />

        <div className="flex items-center gap-2 rounded-lg border border-oak-black/[0.06] px-2 py-1.5">
          <div className="flex h-5 w-5 items-center justify-center rounded bg-oak-black text-white">
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
              <rect
                x="1.5"
                y="3.5"
                width="13"
                height="9"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path d="M1.5 7h13" stroke="currentColor" strokeWidth="1.3" />
            </svg>
          </div>
          <div className="flex-1 text-[10.5px]">
            <div className="font-semibold text-oak-black">Stripe</div>
            <div className="text-oak-black/50">VISA •••• 0608</div>
          </div>
          <span className="text-oak-black/30">›</span>
        </div>

        <div className="mt-2 flex items-center gap-2 rounded-lg border border-oak-black/[0.06] px-2 py-1.5">
          <div className="flex h-5 w-5 items-center justify-center rounded bg-oak-black/[0.06] text-oak-black">
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
              <path
                d="M1.5 10.5V4h8v6.5M9.5 6h3l2 2.5v2h-5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
              <circle cx="4.5" cy="11.5" r="1.25" stroke="currentColor" strokeWidth="1.3" />
              <circle cx="11.5" cy="11.5" r="1.25" stroke="currentColor" strokeWidth="1.3" />
            </svg>
          </div>
          <div className="flex-1 text-[10.5px]">
            <div className="font-semibold text-oak-black">Ground Shipping</div>
            <div className="text-oak-black/50">$5.00 · 3 business days</div>
          </div>
          <span className="text-oak-black/30">›</span>
        </div>

        <div className="mt-3 space-y-1 text-[10.5px]">
          <div className="flex items-center justify-between font-semibold text-oak-black">
            <span>Total due today</span>
            <span>$122.94</span>
          </div>
          <div className="flex items-center justify-between text-oak-black/50">
            <span>Subtotal</span>
            <span>$117.00</span>
          </div>
          <div className="flex items-center justify-between text-oak-black/50">
            <span>Shipping</span>
            <span>$5.00</span>
          </div>
          <div className="flex items-center justify-between text-oak-black/50">
            <span>Tax</span>
            <span>$0.94</span>
          </div>
        </div>

        <div className="mt-3 rounded-lg bg-oak-black py-2.5 text-center text-[12px] font-medium text-white">
          Buy now
        </div>
      </motion.div>
    </div>
  )
}
